import { VStack, Box } from '@cad-arc';
import { DexFormView } from '@mark43/cad-api';
import { useCallback } from 'react';
import useFormPersist from 'react-hook-form-persist';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { DexFormFooter } from './dex-form-footer';
import { DexFormSection } from './dex-form-section';
import { DexQueryRoutingSection } from './dex-query-routing-section';
import { DexFormModel, DexInquiry } from '../types';
import { UseFormReturn } from 'react-hook-form';
import { IntegratedFormMutation } from '../../forms/integrated-form-context';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { isParamsValid } from '../utils';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useAutoFocusFieldName } from './use-auto-focus-field-name';
import { DexFormNameProvider } from './dex-form-name';
import { useSelectedOriAlias } from './use-selected-ori-alias';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useDexResults } from '../results/use-dex-results';
import { useResetDexResultsStoreInitialFrFields } from './use-reset-dex-results-store-initial-fr-fields';

export function DexForm<T extends DexFormModel<DexInquiry>>({
    dexFormView,
    defaultParams,
    form,
    sendQueryMutation,
    eventNumber,
}: {
    dexFormView: DexFormView;
    defaultParams: DexInquiry;
    form: UseFormReturn<T>;
    sendQueryMutation: IntegratedFormMutation<T>;
    eventNumber?: string;
}): React.ReactElement {
    const { multipleOriEnabled, dexPurposeCode } = useEnabledFeatures();
    const { isFirstResponder } = useApplicationType();
    const autoFocusFieldName = useAutoFocusFieldName(dexFormView.sections);
    const { setShouldResetInitialValues, selectedOriAliasId } = useSelectedOriAlias();
    const { dexResultsStore } = useDexResults();

    useResetDexResultsStoreInitialFrFields();

    useFormPersist(`${dexFormView.dexDispatcherForm.toLowerCase()}_form`, {
        watch: form.watch,
        setValue: form.setValue,
        ...(isFirstResponder && !dexResultsStore.frDexPrefillValues
            ? {}
            : { exclude: Object.keys(form.getValues()) }),
    });

    const clearForm = useCallback(() => {
        const resetValues: { params: DexInquiry; oriAliasId?: string } = { params: defaultParams };
        if (multipleOriEnabled) {
            resetValues.oriAliasId = `${form.getValues()?.oriAliasId ?? ''}` || selectedOriAliasId;
        }
        if (dexPurposeCode) {
            resetValues.params.purposeCode = defaultParams.purposeCode ?? '';
        }
        // @ts-expect-error These params are fine.
        form.reset(resetValues);
        setShouldResetInitialValues(true);
    }, [
        multipleOriEnabled,
        defaultParams,
        form,
        setShouldResetInitialValues,
        selectedOriAliasId,
        dexPurposeCode,
    ]);
    const sendAndClearForm = useCallback(() => {
        form.trigger().then((passedValidation) => {
            if (passedValidation) {
                sendQueryMutation.mutateAsync(form.getValues()).then(() => {
                    clearForm();
                });
            }
        });
    }, [clearForm, form, sendQueryMutation]);

    // @ts-expect-error
    const saveErrorMessage = sendQueryMutation?.failureReason?.response?.data?.error;

    return (
        <IntegratedFormProvider
            form={form}
            mutation={sendQueryMutation}
            keepInputsEnabledOnMutationLoading={true}
        >
            <DexFormNameProvider name={dexFormView.dexDispatcherForm}>
                <VStack spacing={0} h="100%" w="100%" overflowY="hidden">
                    <Box
                        h="100%"
                        w="100%"
                        overflowY="auto"
                        paddingLeft="24px"
                        paddingRight="24px"
                        paddingTop="12px"
                    >
                        <FormBody
                            focusForm={isFirstResponder ? false : undefined}
                            saveErrorMessage={saveErrorMessage}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    if (isParamsValid(form.getValues().params, defaultParams)) {
                                        sendAndClearForm();
                                    }
                                }
                            }}
                        >
                            <VStack gap="8px" paddingBottom="24px">
                                <VStack w="100%" paddingTop="12px">
                                    <DexQueryRoutingSection
                                        eventNumber={eventNumber}
                                        dexEntityType={dexFormView.dexEntityType}
                                    />
                                    {dexFormView.queryModifiersSection ? (
                                        <DexFormSection
                                            testId="query-modifiers-section"
                                            section={dexFormView.queryModifiersSection}
                                        />
                                    ) : undefined}
                                </VStack>
                                <VStack w="100%">
                                    {dexFormView.sections.map((section, i) => (
                                        <DexFormSection
                                            section={section}
                                            key={i}
                                            autoFocusField={autoFocusFieldName}
                                        />
                                    ))}
                                </VStack>
                            </VStack>
                        </FormBody>
                    </Box>
                    <DexFormFooter
                        defaultParams={defaultParams}
                        clearForm={clearForm}
                        sendAndClearForm={sendAndClearForm}
                    />
                </VStack>
            </DexFormNameProvider>
        </IntegratedFormProvider>
    );
}
